// src/logica/market/TradesMarket.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, Typography } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { setInformacion } from "../../store/slices/usuarios";
import { Alerta } from "../ux/Alert";
import { tradeHistoryService, sendTradeService, checkUserService } from "../../services/trades";

// Ajusta lista a tu inventario real
const TOKENS = [
  { key: "CF", type: "CF", label: "CF" },
  { key: "Water", type: "ITEM", label: "Water" },
  { key: "Wood", type: "ITEM", label: "Wood" },
  { key: "Carbon", type: "ITEM", label: "Carbon" },
  { key: "Wheat", type: "ITEM", label: "Wheat" },
  { key: "Corn", type: "ITEM", label: "Corn" },
  { key: "Corn Bag", type: "ITEM", label: "Corn Bag" },
  { key: "Wheat Bag", type: "ITEM", label: "Wheat Bag" },
  { key: "Egg", type: "ITEM", label: "Egg" },
  { key: "Milk", type: "ITEM", label: "Milk" },
  { key: "Wheat seed", type: "ITEM", label: "Wheat seed" },
  { key: "Corn seed", type: "ITEM", label: "Corn seed" },
  { key: "Energy", type: "ITEM", label: "Energy" },
];

function getItemQty(info, item) {
  if (!info) return 0;
  const it = info?.inventario?.find((x) => String(x.item) === String(item));
  return Number(it?.cantidad || 0);
}

export const TradesMarket = ({ handleClose4 }) => {
  const { informacion } = useSelector((s) => s.userSlice);
  const dispatch = useDispatch();

  const [scope, setScope] = useState("all");
  const [history, setHistory] = useState([]);

  const [to, setTo] = useState("");
  const [toCheck, setToCheck] = useState({ status: "idle", exists: false, display: "" }); // idle|loading|ok|bad
  const [tokenKey, setTokenKey] = useState("Water");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);

  const [openAlert, setOpenAlert] = useState(false);
  const [error, setError] = useState(false);
  const [mensaje, setMensaje] = useState("");

  const selectedToken = useMemo(
    () => TOKENS.find((t) => t.key === tokenKey) || TOKENS[0],
    [tokenKey]
  );

  const available = useMemo(() => {
    if (!informacion) return 0;
    if (selectedToken.type === "CF") return Number(informacion.cf || 0);
    return getItemQty(informacion, selectedToken.key);
  }, [informacion, selectedToken]);

  const remaining = useMemo(() => {
    const n = Number(amount || 0);
    if (!Number.isFinite(n) || n <= 0) return available;
    return Math.max(available - n, 0);
  }, [available, amount]);

  const canSend = useMemo(() => {
    const n = Number(amount || 0);
    if (!to.trim()) return false;
    if (toCheck.status !== "ok" || !toCheck.exists) return false;
    if (!Number.isFinite(n) || n <= 0) return false;
    if (n > available) return false;
    return true;
  }, [to, toCheck, amount, available]);

  const showAlert = (msg, isErr = false) => {
    setMensaje(msg);
    setError(isErr);
    setOpenAlert(true);
  };

  const loadHistory = async () => {
    try {
      const res = await tradeHistoryService(scope, 120);
      setHistory(res.data || []);
    } catch (e) {
      // no molestar UI
    }
  };

  // ✅ Debounce check user
  useEffect(() => {
    const q = to.trim();
    if (q.length < 3) {
      setToCheck({ status: "idle", exists: false, display: "" });
      return;
    }

    setToCheck((p) => ({ ...p, status: "loading" }));

    const t = setTimeout(async () => {
      try {
        const res = await checkUserService(q);
        if (res?.data?.exists) {
          setToCheck({ status: "ok", exists: true, display: res.data.display || q });
        } else {
          setToCheck({ status: "bad", exists: false, display: "" });
        }
      } catch (e) {
        setToCheck({ status: "bad", exists: false, display: "" });
      }
    }, 450);

    return () => clearTimeout(t);
  }, [to]);

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const send = async () => {
    try {
      if (!canSend) return;

      const n = Number(amount);
      const payload = {
        to: to.trim(),
        tokenType: selectedToken.type, // "CF"|"ITEM"
        tokenKey: selectedToken.type === "CF" ? "CF" : selectedToken.key,
        amount: n,
        note: note.trim(),
        idemKey: uuidv4(),
      };

      const res = await sendTradeService(payload);

      if (res?.data?.informacion) dispatch(setInformacion(res.data.informacion));

      setTo("");
      setAmount("");
      setNote("");
      setConfirmOpen(false);

      showAlert("SUCCESS ✅", false);
      loadHistory();
    } catch (e) {
      const msg = e?.response?.data?.error || "ERROR";
      showAlert(msg, true);
      setConfirmOpen(false);
      loadHistory();
    }
  };

  const fmtWhen = (d) => {
    try {
      return d ? new Date(d).toLocaleString() : "";
    } catch {
      return "";
    }
  };

  const myKey = useMemo(() => {
    const a = (informacion?.nombre || informacion?.correo || "").toLowerCase().trim();
    return a;
  }, [informacion]);

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "auto", position: "relative" }}>
      <Box
        onClick={() => handleClose4?.()}
        sx={{
          position: "absolute",
          fontSize: "18px",
          right: 12,
          top: 8,
          zIndex: 100000000001,
          color: "gray",
          cursor: "pointer",
        }}
      >
        X
      </Box>

      <Box className="rpgui-container framed-golden" sx={{ width: "100%", p: 1 }}>
        <Typography sx={{ fontWeight: 900, color: "#fff" }}>TRADES</Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          width: "100%",
          gap: 2,
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Send panel */}
        <Box className="rpgui-container framed-golden-2-market" sx={{ flex: 1, minHeight: 420 }}>
          <Typography sx={{ mb: 1 }}>Send Trade</Typography>

          <Typography sx={{ fontSize: 12, opacity: 0.9 }}>To (username or email)</Typography>
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="receiver"
            style={{ width: "100%" }}
          />

          <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
            {toCheck.status === "loading" && (
              <Typography sx={{ fontSize: 12, opacity: 0.8 }}>Checking...</Typography>
            )}
            {toCheck.status === "ok" && toCheck.exists && (
              <Typography sx={{ fontSize: 12, color: "#7CFF9C", fontWeight: 900 }}>
                ✅ User exists: {toCheck.display}
              </Typography>
            )}
            {toCheck.status === "bad" && (
              <Typography sx={{ fontSize: 12, color: "#FF6B6B", fontWeight: 900 }}>
                ❌ User not found
              </Typography>
            )}
          </Box>

          <Typography sx={{ fontSize: 12, opacity: 0.9, mt: 1 }}>Token</Typography>
          <select
            value={tokenKey}
            onChange={(e) => setTokenKey(e.target.value)}
            className="rpgui-dropdown"
          >
            {TOKENS.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>

          <Typography sx={{ fontSize: 12, opacity: 0.9, mt: 1 }}>Amount</Typography>
          <input
            value={amount}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "") return setAmount("");
              if (Number(v) > 0) setAmount(v);
            }}
            placeholder="Quantity"
            type="number"
            style={{ width: "100%" }}
          />

          <Typography sx={{ fontSize: 12, opacity: 0.9, mt: 1 }}>Note (optional)</Typography>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="message..."
            style={{ width: "100%" }}
          />

          <Box className="rpgui-container" sx={{ p: 1, mt: 1 }}>
            <Typography sx={{ fontSize: 12 }}>
              Available: <b>{available}</b> {selectedToken.type === "CF" ? "CF" : selectedToken.key}
            </Typography>
            <Typography sx={{ fontSize: 12 }}>
              Remaining: <b>{remaining}</b>
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Box sx={{ filter: canSend ? "none" : "grayscale(100%)", pointerEvents: canSend ? "auto" : "none" }}>
              <Button className="rpgui-button" onClick={() => setConfirmOpen(true)}>
                <p>Send</p>
              </Button>
            </Box>

            <Button
              className="rpgui-button"
              onClick={() => {
                setTo("");
                setAmount("");
                setNote("");
                setToCheck({ status: "idle", exists: false, display: "" });
              }}
            >
              <p>Clear</p>
            </Button>

            <Button className="rpgui-button-m" onClick={loadHistory}>
              <p>Refresh</p>
            </Button>
          </Box>

          {!canSend && (
            <Typography sx={{ fontSize: 11, opacity: 0.8, mt: 1 }}>
              Tip: el botón se activa solo si el usuario existe y la cantidad ≤ disponible.
            </Typography>
          )}
        </Box>

        {/* History panel */}
        <Box className="rpgui-container framed-golden-2-market" sx={{ flex: 1, minHeight: 420 }}>
          <Typography sx={{ mb: 1 }}>Trade History</Typography>

          <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <Button className="rpgui-button-m" onClick={() => setScope("all")}>
              <p>All</p>
            </Button>
            <Button className="rpgui-button-m" onClick={() => setScope("sent")}>
              <p>Sent</p>
            </Button>
            <Button className="rpgui-button-m" onClick={() => setScope("received")}>
              <p>Received</p>
            </Button>
          </Box>

          <Box sx={{ height: 320, overflow: "auto" }}>
            {history?.length === 0 ? (
              <Typography sx={{ fontSize: 12, opacity: 0.8 }}>No trades yet.</Typography>
            ) : (
              history.map((h) => {
                const fromU = String(h.fromUser || "").toLowerCase().trim();
                const isSent = myKey && fromU === myKey;

                const badge = h.status === "OK" ? "✅" : "❌";
                const dir = isSent ? "Sent" : "Received";

                return (
                  <Box
                    key={h._id || uuidv4()}
                    sx={{
                      fontSize: 12,
                      borderBottom: "1px solid rgba(200,200,200,0.25)",
                      p: 1,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <b>
                        {badge} {dir}
                      </b>
                      <span style={{ opacity: 0.75 }}>{fmtWhen(h.createdAt)}</span>
                    </div>

                    <div style={{ opacity: 0.9 }}>
                      <b>{h.amount}</b> {h.tokenKey}{" "}
                      {isSent ? (
                        <span>
                          → <b>{h.toUser}</b>
                        </span>
                      ) : (
                        <span>
                          ← <b>{h.fromUser}</b>
                        </span>
                      )}
                    </div>

                    {h.note ? <div style={{ opacity: 0.8 }}>“{h.note}”</div> : null}
                    {h.status !== "OK" ? <div style={{ opacity: 0.8 }}>Reason: {h.reason}</div> : null}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <Box
          sx={{
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontFamily: "Minecraft",
            letterSpacing: "1.5px",
            fontSize: "1.1rem",
          }}
          className="rpgui-container framed-golden"
        >
          <p>
            Confirm sending <b>{amount || 0}</b> <b>{selectedToken.type === "CF" ? "CF" : selectedToken.key}</b> to{" "}
            <b>{to || "?"}</b>
          </p>
          {note ? <p style={{ opacity: 0.85 }}>Note: “{note}”</p> : null}

          <Box sx={{ display: "flex", gap: 1 }}>
            <button onClick={send} className="rpgui-button">
              <Typography sx={{ color: "#fff" }}>I agree</Typography>
            </button>
            <button onClick={() => setConfirmOpen(false)} className="rpgui-button">
              <Typography sx={{ color: "#fff" }}>Cancel</Typography>
            </button>
          </Box>
        </Box>
      </Dialog>

      <Alerta mensaje={mensaje} open={openAlert} handleClose={() => setOpenAlert(false)} error={error} />
    </Box>
  );
};
